/**
 * Utility functions for document handling
 */
import * as UTIF from 'utif';

/**
 * Handle TIFF image in a new tab
 * Converts TIFF to canvas and displays in new window
 * @param {string} base64Data - The base64 encoded TIFF data
 */
const handleTiffInNewTab = async (base64Data) => {
  try {
    // Convert base64 to array buffer
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const buffer = bytes.buffer;

    // Decode TIFF
    const ifds = UTIF.decode(buffer);
    if (!ifds.length) throw new Error('No image data found in TIFF');

    UTIF.decodeImage(buffer, ifds[0]);
    const rgba = UTIF.toRGBA8(ifds[0]);

    // Create canvas
    const canvas = document.createElement('canvas');
    canvas.width = ifds[0].width;
    canvas.height = ifds[0].height;

    const ctx = canvas.getContext('2d');
    const imageData = new ImageData(
      new Uint8ClampedArray(rgba),
      ifds[0].width,
      ifds[0].height
    );
    ctx.putImageData(imageData, 0, 0);

    // Convert canvas to data URL
    const imgDataUrl = canvas.toDataURL('image/png');

    // Open in new tab
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>TIFF Image Preview</title>
            <style>
              body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #f0f0f0; }
              img { max-width: 100%; max-height: 100vh; object-fit: contain; }
            </style>
          </head>
          <body>
            <img src="${imgDataUrl}" alt="TIFF Image" />
          </body>
        </html>
      `);
      newWindow.document.close();
    }
  } catch (error) {
    console.error('Error opening TIFF:', error);
    alert('Failed to open TIFF image. Please try again.');
  }
};

/**
 * Open document in a new tab
 * Handles data URIs by converting large documents to blob URLs
 * @param {string} dataUri - The data URI of the document
 * @param {string} docType - The document type (image, pdf, etc.)
 */
export const openDocumentInNewTab = (dataUri, docType) => {
  try {
    // Extract base64 data and mime type
    const matches = dataUri.match(/^data:([^;]+);base64,(.+)$/);
    if (!matches) {
      console.error('Invalid data URI format');
      return;
    }

    const mimeType = matches[1];
    const base64Data = matches[2];

    // Special handling for TIFF images
    if (
      docType === 'tiff' ||
      mimeType.includes('tiff') ||
      mimeType.includes('tif')
    ) {
      handleTiffInNewTab(base64Data);
      return;
    }

    // Convert base64 to binary
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Create blob and open in new tab
    const blob = new Blob([bytes], { type: mimeType });
    const blobUrl = URL.createObjectURL(blob);

    // Open in new tab
    const newWindow = window.open('', '_blank');

    if (newWindow) {
      // Write HTML content to display the document properly
      if (docType === 'image') {
        newWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Image Preview</title>
              <style>
                body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #f0f0f0; }
                img { max-width: 100%; max-height: 100vh; object-fit: contain; }
              </style>
            </head>
            <body>
              <img src="${blobUrl}" alt="Document" />
            </body>
          </html>
        `);
      } else {
        // For PDFs and other documents, set the location
        newWindow.location.href = blobUrl;
      }

      newWindow.document.close();
    }

    // Clean up blob URL after a longer delay to ensure it loads
    setTimeout(() => {
      URL.revokeObjectURL(blobUrl);
    }, 10000); // 10 seconds to ensure document loads properly
  } catch (error) {
    console.error('Error opening document:', error);
    alert('Failed to open document. Please try again.');
  }
};
